import React, { useState, useRef, useEffect, useCallback } from 'react';
import screenfull from 'screenfull';
import './index.css';
import videoList from './data';
import videoListEn from './data_en';
import videoListVi from './data_vi';
import { useTranslation } from 'react-i18next';
import SEO from '../seo';
import { PAGE_SEO, COMMON_KEYWORDS } from '../seo/pageSeo';

// ── SVG Icons ──
const PlayIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
);

const FullscreenIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>
);

const ExitFullscreenIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 8h-2v3h3v2h-5V9h2v5zm4-5h2v-3h-3v2h5V9h-4v3z" /></svg>
);

const VolumeIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" /></svg>
);

// ── Custom Video Player ──
function VideoPlayer({ video, onVideoEnd }) {
  const videoEl = useRef(null);
  const wrapperEl = useRef(null);
  const progressEl = useRef(null);
  const hideTimer = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = useCallback((e) => {
    if (!progressEl.current || !videoEl.current) return;
    const rect = progressEl.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    videoEl.current.currentTime = ratio * duration;
  }, [duration]);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (playing) setShowControls(false);
    }, 2500);
  }, [playing]);

  const togglePlay = useCallback(() => {
    if (!videoEl.current) return;
    if (playing) {
      videoEl.current.pause();
    } else {
      videoEl.current.play();
    }
    setPlaying(!playing);
  }, [playing]);

  const handleFullscreen = useCallback(() => {
    if (!wrapperEl.current) return;
    if (!isFullscreen) {
      if (screenfull.isEnabled) {
        screenfull.request(wrapperEl.current);
      }
    } else {
      if (screenfull.isEnabled && screenfull.isFullscreen) {
        screenfull.exit();
      }
    }
  }, [isFullscreen]);

  useEffect(() => {
    const vf = videoEl.current;
    if (!vf) return;

    const onPlay = () => setPlaying(true);
    const onPause = () => { setPlaying(false); setShowControls(true); };
    const onEnded = () => {
      setPlaying(false);
      setPlayed(0);
      onVideoEnd?.();
    };
    const onTimeUpdate = () => {
      setPlayed(vf.currentTime);
      if (vf.buffered.length > 0) {
        setBuffered(vf.buffered.end(vf.buffered.length - 1));
      }
    };
    const onDurationChange = () => setDuration(vf.duration || 0);
    const onFullscreenChange = () => setIsFullscreen(!!screenfull.isFullscreen);

    vf.addEventListener('play', onPlay);
    vf.addEventListener('pause', onPause);
    vf.addEventListener('ended', onEnded);
    vf.addEventListener('timeupdate', onTimeUpdate);
    vf.addEventListener('durationchange', onDurationChange);

    if (screenfull.isEnabled) {
      screenfull.on('change', onFullscreenChange);
    }

    return () => {
      vf.removeEventListener('play', onPlay);
      vf.removeEventListener('pause', onPause);
      vf.removeEventListener('ended', onEnded);
      vf.removeEventListener('timeupdate', onTimeUpdate);
      vf.removeEventListener('durationchange', onDurationChange);
      if (screenfull.isEnabled) {
        screenfull.off('change', onFullscreenChange);
      }
    };
  }, [onVideoEnd]);

  // Reset on video change
  useEffect(() => {
    if (videoEl.current) {
      videoEl.current.currentTime = 0;
      setPlayed(0);
      setPlaying(false);
    }
  }, [video?.url]);

  const progressPct = duration > 0 ? (played / duration) * 100 : 0;
  const bufferedPct = duration > 0 ? (buffered / duration) * 100 : 0;

  return (
    <div
      className={`vg-player ${showControls || !playing ? 'controls-visible' : ''}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => playing && setShowControls(false)}
      onClick={togglePlay}
    >
      <div ref={wrapperEl} style={{ position: 'absolute', inset: 0 }}>
        <video
          ref={videoEl}
          src={video?.url}
          poster={video?.thumbnail}
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
        />
      </div>

      {/* Brand badge */}
      <div className="vg-player__brand">
        <span>XIAOMAI</span>
      </div>

      {/* Play button (shown when paused) */}
      {!playing && (
        <div className="vg-player__play-btn">
          <div className="vg-player__play-circle">
            <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" fill="#ffb700" /></svg>
          </div>
        </div>
      )}

      {/* Controls */}
      <div
        className={`vg-controls ${showControls || !playing ? 'visible' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress */}
        <div
          className="vg-progress"
          ref={progressEl}
          onClick={handleProgressClick}
        >
          <div className="vg-progress__buffered" style={{ width: `${bufferedPct}%` }} />
          <div className="vg-progress__played" style={{ width: `${progressPct}%` }} />
          <div className="vg-progress__thumb" style={{ left: `${progressPct}%` }} />
        </div>

        {/* Buttons row */}
        <div className="vg-controls-row">
          <button className="vg-ctrl-btn vg-ctrl-btn--play" onClick={togglePlay}>
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>

          <button className="vg-ctrl-btn">
            <VolumeIcon />
          </button>

          <span className="vg-ctrl-time">
            {formatTime(played)} / {formatTime(duration)}
          </span>

          <button className="vg-ctrl-btn vg-ctrl-btn--fullscreen" onClick={handleFullscreen}>
            {isFullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ──
export default function SpreadersVideo() {
  const { t, i18n } = useTranslation();
  const [currentVideo, setCurrentVideo] = useState(null);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    let list;
    if (i18n.language.startsWith('vi')) {
      list = videoListVi;
    } else if (i18n.language.startsWith('en')) {
      list = videoListEn;
    } else {
      list = videoList;
    }
    setVideos(list);
    if (list.length > 0 && !currentVideo) {
      setCurrentVideo(list[0]);
    }
  }, [i18n.language]);

  const handleSelect = (video) => {
    setCurrentVideo(video);
  };

  const handleVideoEnd = useCallback(() => {
    if (!currentVideo || videos.length === 0) return;
    const idx = videos.findIndex((v) => v.id === currentVideo.id);
    const next = videos[(idx + 1) % videos.length];
    setCurrentVideo(next);
  }, [currentVideo, videos]);

  return (
    <>
    <SEO
        pageKey="spreaders_video"
        title={PAGE_SEO.spreaders_video.title}
        description={PAGE_SEO.spreaders_video.description}
        keywords={COMMON_KEYWORDS}
        slug={PAGE_SEO.spreaders_video.slug}
    />
    <div className="video-gallery-page">
      {/* Hero */}
      <div className="vg-hero">
        <div className="vg-hero__bg" />
        <div className="vg-hero__overlay" />
        <div className="vg-hero__content">
          <div className="vg-hero__eyebrow">XIAOMAI</div>
          <h1 className="vg-hero__title">{t('videoHighlights')}</h1>
          <div className="vg-hero__rule" />
        </div>
      </div>

      <div className="vg-container">
        {/* Player */}
        <section className="vg-player-section">
          <VideoPlayer
            key={currentVideo?.id}
            video={currentVideo}
            onVideoEnd={handleVideoEnd}
          />
        </section>

        {/* Thumbnail grid */}
        <section className="vg-grid-section">
          <div className="vg-section-header">
            <div className="vg-section-title">
              {t('spreadervideo_all_videos')}
            </div>
            <div className="vg-section-rule" />
          </div>

          <div className="vg-thumb-grid">
            {videos.map((video) => (
              <div
                key={video.id}
                className={`vg-thumb-card ${currentVideo?.id === video.id ? 'selected' : ''}`}
                onClick={() => handleSelect(video)}
              >
                <img
                  className="vg-thumb-img"
                  src={video.thumbnail}
                  alt={video.title}
                  loading="lazy"
                />
                <div className="vg-thumb-overlay">
                  <p className="vg-thumb-title">{video.title}</p>
                </div>
                <div className="vg-thumb-play-icon">
                  <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
    </>
  );
}
